/**
 * LockOverlay — fullscreen PIN gate shown when the idle-lock fires.
 *
 * Mounted at the top of Shell with z-index above everything. While visible:
 *   - PIN input centered; correct PIN → dismisses the overlay.
 *   - 5 wrong PINs in a row → force a real sign-out (the safety valve;
 *     server-side session is cleared, browser redirects to /login).
 *   - F12 / Ctrl+Shift+I / Ctrl+Shift+J / Ctrl+U keydowns are preventDefault'd —
 *     a casual deterrent. **This does NOT meaningfully block DevTools**: the
 *     browser menu still opens it. The lock is walk-away protection, not real
 *     security; the actual safety net is the force-logout timer.
 *
 * Props: none. Reads `locked` from lib/lock.js, dismisses via unlock().
 */
import { createSignal, createEffect, onCleanup, Show } from 'solid-js';
import { locked, unlock } from '../lib/lock.js';
import { verifyPin } from '../lib/pin.js';
import { api } from '../lib/api.js';

const MAX_ATTEMPTS = 5;

export default function LockOverlay() {
  const [pin, setPin]         = createSignal('');
  const [err, setErr]         = createSignal('');
  const [tries, setTries]     = createSignal(0);
  const [busy, setBusy]       = createSignal(false);
  let inputEl;

  // Best-effort DevTools/keyboard deterrent — see file header for honesty about
  // its limits. Only attaches while the overlay is visible.
  const onKey = (e) => {
    if (e.key === 'F12'
        || (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i' || e.key === 'J' || e.key === 'j' || e.key === 'C' || e.key === 'c'))
        || (e.ctrlKey && (e.key === 'u' || e.key === 'U'))) {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  const onCtx = (e) => { e.preventDefault(); };

  createEffect(() => {
    if (!locked()) return;
    // Reset for a fresh prompt; focus the input after mount.
    setPin(''); setErr(''); setTries(0);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('contextmenu', onCtx, true);
    queueMicrotask(() => inputEl?.focus());
  });
  onCleanup(() => {
    document.removeEventListener('keydown', onKey, true);
    document.removeEventListener('contextmenu', onCtx, true);
  });

  const submit = async (e) => {
    e?.preventDefault();
    const p = pin().trim();
    if (!p) return;
    setBusy(true);
    try {
      const r = await verifyPin(p);
      if (r?.ok) {
        unlock();
        return;
      }
      const n = tries() + 1;
      setTries(n);
      setPin('');
      if (n >= MAX_ATTEMPTS) {
        // Safety valve: too many wrong PINs → real sign-out. Cooperates with the
        // independent force-logout timer.
        setErr(`Too many wrong PIN attempts — signing out.`);
        try { await api.post('/api/auth/logout'); } catch (_) {}
        location.href = '/login';
        return;
      }
      setErr(`Wrong PIN. ${MAX_ATTEMPTS - n} attempts left.`);
      queueMicrotask(() => inputEl?.focus());
    } catch (e) {
      setErr('Couldn’t verify the PIN: ' + (e?.message || 'network error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Show when={locked()}>
      <div class="lock-overlay" role="dialog" aria-modal="true">
        <div class="lock-card">
          <h2 class="lock-title">🔒 Locked</h2>
          <p class="lock-sub muted">Enter your PIN to continue.</p>
          <form onSubmit={submit}>
            <input
              ref={inputEl}
              class="input lock-pin mono"
              type="password"
              inputmode="numeric"
              autocomplete="off"
              maxlength="6"
              disabled={busy()}
              value={pin()}
              onInput={(e) => setPin(e.target.value.replace(/[^0-9]/g, ''))}
              autofocus
            />
            <Show when={err()}>
              <div class="lock-err">{err()}</div>
            </Show>
            <button class="btn lock-submit" type="submit" disabled={busy() || !pin()}>
              Unlock
            </button>
          </form>
        </div>
      </div>
    </Show>
  );
}
